import dayjs from 'dayjs';
import { type ReactNode, useCallback, useState } from 'react';
import { ConversationContextResponse } from 'src/graphql/entities/conversation';
import type { ExportDocument } from 'src/pages/Conversations/document';
import {
  PDF_CONTENT_WIDTH_PT,
  PDF_IMAGE_DIMENSION_TIMEOUT_MS,
  PDF_IMAGE_DPI_SCALE,
  PDF_MAX_IMAGE_HEIGHT_PT,
} from 'src/pages/Conversations/pdf/constants';
import { ConversationPdfExportTemplate } from 'src/pages/Conversations/pdf/conversation-pdf-export-template';
import { buildConversationPdfFileName } from 'src/pages/Conversations/pdf/conversation-pdf-file-name';
import { loadPortalLogoImage } from 'src/pages/Conversations/pdf/portal-logo';
import { downloadBlob } from 'src/utils/file';

export interface ExportConversationPdfInput {
  context: ConversationContextResponse;
  conversation: {
    friendlyId?: string | null;
    subject?: string | null;
  };
  document: ExportDocument;
}

interface UseConversationPdfExportResult {
  exportConversationAsPdf: (input: ExportConversationPdfInput) => Promise<void>;
  isExporting: boolean;
}

interface PdfRendererModule {
  pdf: (document: ReactNode) => {
    toBlob: () => Promise<Blob>;
  };
}

const extractDocumentImageSources = (document: ExportDocument): string[] => {
  const sources: string[] = [];
  for (const section of document.sections) {
    for (const element of section.elements) {
      if (element.type === 'image') {
        sources.push(element.src);
      }
    }
  }
  return sources;
};

export const useConversationPdfExport = (): UseConversationPdfExportResult => {
  const [isExporting, setIsExporting] = useState(false);

  const exportConversationAsPdf = useCallback(
    async ({ context, conversation, document }: ExportConversationPdfInput): Promise<void> => {
      setIsExporting(true);

      try {
        const exportDate = new Date();
        const fileName = buildConversationPdfFileName({
          friendlyId: conversation.friendlyId,
          subject: context.subject ?? conversation.subject,
          exportedAt: exportDate,
        });
        const resolvedImageDimensions = await buildResolvedImageDimensionsMap(document);
        const logoImage = await loadPortalLogoImage();
        const { pdf } = (await import('@react-pdf/renderer')) as PdfRendererModule;
        const blob = await pdf(
          <ConversationPdfExportTemplate
            document={document}
            exportDateLabel={buildExportDateLabel(exportDate)}
            logoDataUrl={logoImage?.dataUrl ?? null}
            logoDimensions={
              logoImage ? { height: logoImage.height, width: logoImage.width } : undefined
            }
            resolvedImageDimensions={resolvedImageDimensions}
          />,
        ).toBlob();

        if (!(blob instanceof Blob)) {
          throw new Error('Unable to create PDF file.');
        }

        downloadBlob(blob, fileName);
      } finally {
        setIsExporting(false);
      }
    },
    [],
  );

  return {
    exportConversationAsPdf,
    isExporting,
  };
};

function buildExportDateLabel(exportDate: Date): string {
  return `Exported ${dayjs(exportDate).format('MMMM D, YYYY')}`;
}

function resolvePdfImageDimensions(
  src: string,
): Promise<{ height: number; width: number } | null> {
  return new Promise<{ height: number; width: number } | null>((resolve) => {
    const image = new window.Image();
    const timeout = window.setTimeout(() => {
      resolve(null);
    }, PDF_IMAGE_DIMENSION_TIMEOUT_MS);

    const clear = (): void => {
      window.clearTimeout(timeout);
    };

    image.crossOrigin = 'anonymous';
    image.onload = () => {
      clear();
      const intrinsicWidthPt = image.naturalWidth * PDF_IMAGE_DPI_SCALE;
      const intrinsicHeightPt = image.naturalHeight * PDF_IMAGE_DPI_SCALE;
      const scale = Math.min(
        1,
        PDF_CONTENT_WIDTH_PT / intrinsicWidthPt,
        PDF_MAX_IMAGE_HEIGHT_PT / intrinsicHeightPt,
      );

      resolve({
        width: intrinsicWidthPt * scale,
        height: intrinsicHeightPt * scale,
      });
    };
    image.onerror = () => {
      clear();
      resolve(null);
    };
    image.src = src;
  });
}

async function buildResolvedImageDimensionsMap(
  document: ExportDocument,
): Promise<Record<string, { height: number; width: number }>> {
  const sources = [...new Set(extractDocumentImageSources(document))];
  const resolvedEntries = await Promise.all(
    sources.map(async (src) => {
      const dimensions = await resolvePdfImageDimensions(src);
      return dimensions ? ([src, dimensions] as const) : null;
    }),
  );

  return Object.fromEntries(
    resolvedEntries.filter(
      (entry): entry is readonly [string, { height: number; width: number }] => entry !== null,
    ),
  );
}
